import { GET_ADM as GET, POST_ADM as POST, PUT_ADM as PUT } from '../../../../services/api'
import { 
  Session, 
  Message, 
  CommunicationStats, 
  SearchParams, 
  SearchResult,
  SentimentAnalysis,
  TranslationRequest,
  TranslationResponse,
  AIResponseSuggestion,
  Department
} from '../types/communication.types'

// Base URL para todos os endpoints da Central de Comunicação  
// Nota: Removemos '/api' porque a baseURL do axios já inclui '/api'
const BASE_URL = 'communication-center'

// 🐛 INTERCEPTADOR GLOBAL PARA CAPTURAR CHAMADAS COM 'messages'
const originalGET = GET

// Substituir temporariamente as funções GET/POST/PUT para capturar URLs suspeitas
const GET_INTERCEPTED = (url: string) => {
  // 🔍 Log TODAS as chamadas GET
  console.log('🌐 GET interceptado:', url)
  
  // 🚨 Verificar se URL contém o padrão problemático
  if (url.includes('/sessions/') && url.endsWith('/messages')) {
    console.error('🚨 ENCONTRADO! URL PROBLEMÁTICA DETECTADA:', url)
    console.error('🚨 URL completa:', url)
    console.trace('🚨 STACK TRACE DA CHAMADA PROBLEMÁTICA:')
    
    // Tentar extrair hotelId da URL para debug
    const match = url.match(/\/sessions\/(\d+)\/messages/)
    if (match) {
      const hotelId = match[1]
      console.error(`🚨 Detectado: getSessionDetails(${hotelId}, "messages") <- ESTE É O PROBLEMA!`)
    }
  }
  
  // Usar apenas a URL (função GET_ADM espera apenas um parâmetro)
  return originalGET(url)
}

export class CommunicationAPI {
  
  // ===== SESSÕES =====
  
  /**
   * Listar todas as sessões ativas
   */
  static async getSessions(hotelId?: number): Promise<Session[]> {
    // Se hotelId não fornecido, usar um padrão para teste
    const targetHotelId = hotelId || 2826
    console.log('🏨 Buscando sessões para hotel:', targetHotelId)
    return GET_INTERCEPTED(`${BASE_URL}/sessions/${targetHotelId}`)
  }

  /**
   * Buscar sessões com mensagens não lidas de um hotel específico
   */
  static async getUnreadSessions(hotelId: number): Promise<Session[]> {
    return GET_INTERCEPTED(`${BASE_URL}/sessions/unread/${hotelId}`)
  }

  /**
   * Obter detalhes de uma sessão específica
   */
  static async getSessionDetails(hotelId: number, sessionKey: string): Promise<Session> {
    // 🐛 DEBUG: Adicionar log para capturar chamadas incorretas
    console.log('🔍 DEBUG getSessionDetails chamado com:', { hotelId, sessionKey })
    
    // Verificar se sessionKey é válido
    if (sessionKey === 'messages' || !sessionKey || sessionKey.length < 10) {
      console.error('❌ ERRO: SessionKey inválido detectado!', { hotelId, sessionKey })
      console.trace('Stack trace da chamada:')
      throw new Error(`SessionKey inválido: "${sessionKey}". Deve ser uma sessionKey real como "session-2826-2826-alessandro-1761621907353-q1ipni"`)
    }
    
    return  GET_INTERCEPTED(`${BASE_URL}/sessions/${hotelId}/${sessionKey}`)
  }

  /**
   * Marcar mensagens de uma sessão como lidas
   */
  static async markSessionAsRead(hotelId: number, sessionKey: string): Promise<void> {
    return PUT(`${BASE_URL}/sessions/${hotelId}/${sessionKey}/read`, {})
  }

  /**
   * Atualizar modo concierge de uma sessão
   */
  static async updateConciergeMode(params: {
    sessionKey: string
    hotelId: number
    room: string
    reservationId: string
    conciergeMode: 'manual' | 'assisted' | 'autopilot'
    pluginspaceId: number
  }): Promise<void> {
    console.log('🤖 CommunicationAPI: Chamando POST /conciergeModeInfo com:', params)
    
    try {
      const response = await POST(`${BASE_URL}/conciergeModeInfo`, params)
      console.log('✅ CommunicationAPI: Resposta do backend:', response)
      return response
    } catch (error) {
      console.error('❌ CommunicationAPI: Erro ao atualizar modo concierge:', error)
      throw error
    }
  }

  // ===== MENSAGENS =====

  /**
   * Obter histórico completo de mensagens de uma sessão
   */
  static async getMessages(hotelId: number, sessionKey: string): Promise<Message[]> {
    // 🐛 DEBUG: Log para identificar chamadas incorretas
    console.log('🔍 DEBUG getMessages chamado com:', { hotelId, sessionKey })
    
    // Verificar se sessionKey é válido
    if (sessionKey === 'messages' || !sessionKey || sessionKey.length < 10) {
      console.error('❌ ERRO: SessionKey inválido no getMessages!', { hotelId, sessionKey })
      console.trace('Stack trace da chamada:')
      throw new Error(`SessionKey inválido no getMessages: "${sessionKey}". Deve ser uma sessionKey real.`)
    }
    
    try {
      // Tentar com o BASE_URL normal primeiro
      return await GET_INTERCEPTED(`${BASE_URL}/messages/${hotelId}/${sessionKey}`)
    } catch (error) {
      console.log('⚠️ Tentativa 1 falhou:', error)
      console.log('⚠️ Tentando URL direta...')
      // Fallback: usar URL direta que sabemos que funciona
      return await GET_INTERCEPTED(`/communication-center/messages/${hotelId}/${sessionKey}`)
    }
  }

  /**
   * 🔄 NOVO: Obter histórico agregado de todas as sessões de uma reserva
   */
  static async getReservationMessages(hotelId: number, reservationId: string): Promise<Message[]> {
    console.log('🔍 DEBUG getReservationMessages:', { hotelId, reservationId })
    
    try {
      // Nova API que busca mensagens de todas as sessões da reserva
      return await GET_INTERCEPTED(`${BASE_URL}/messages/reservation/${hotelId}/${reservationId}`)
    } catch (error) {
      console.warn('⚠️ API de reserva não disponível, usando método de sessão única:', error)
      throw error
    }
  }

  /**
   * 🔄 NOVO: Obter histórico agregado por hóspede (CPF/Room)
   */
  static async getGuestMessages(hotelId: number, guestIdentifier: string): Promise<Message[]> {
    console.log('🔍 DEBUG getGuestMessages:', { hotelId, guestIdentifier })
    
    try {
      // Nova API que busca mensagens de todas as sessões do hóspede
      return await GET_INTERCEPTED(`${BASE_URL}/messages/guest/${hotelId}/${guestIdentifier}`)
    } catch (error) {
      console.warn('⚠️ API de hóspede não disponível, usando método de sessão única:', error)
      throw error
    }
  }

  /**
   * Obter mensagens recentes (últimos 30 minutos) de uma sessão
   */
  static async getRecentMessages(hotelId: number, sessionKey: string): Promise<Message[]> {
    return GET_INTERCEPTED(`${BASE_URL}/messages/${hotelId}/${sessionKey}/recent`)
  }

  /**
   * 🆕 Buscar mensagens por channel/departamento
   * Retorna todas as mensagens de um departamento específico
   */
  static async getMessagesByChannel(
    hotelId: number, 
    channel: Department,
    days: number = 3,
    limit: number = 100,
    offset: number = 0
  ): Promise<any> {
    const params = new URLSearchParams({
      channel,
      days: days.toString(),
      limit: limit.toString(),
      offset: offset.toString()
    })
    
    console.log(`🎯 Buscando mensagens do channel ${channel} para hotel ${hotelId}`)
    return GET_INTERCEPTED(`${BASE_URL}/messages/by-channel/${hotelId}?${params.toString()}`)
  }

  /**
   * Atualizar status de uma mensagem específica
   */
  static async updateMessageStatus(
    messageId: number, 
    status: 'sent' | 'delivered' | 'read' | 'failed'
  ): Promise<void> {
    return PUT(`${BASE_URL}/messages/${messageId}/status`, { status })
  }

  /**
   * Enviar mensagem para uma sessão (DEPRECIADO - usar sendAdminMessage)
   */
  static async sendMessage(
    hotelId: number,
    sessionKey: string,
    text: string,
    imageUrl?: string
  ): Promise<Message> {
    return POST(`${BASE_URL}/messages/${hotelId}/${sessionKey}`, {
      text,
      imageUrl,
      author: 'agent'
    })
  }

  /**
   * Enviar mensagem do Admin via WebSocket (salva no banco automaticamente)
   */
  static async sendAdminMessage(
    sessionId: number,
    message: string,
    imageUrl?: string,
    channel: string = 'recepcao',
    agentData?: {
      agentId: string,
      agentEmail: string,
      agentName: string,
      hotelId: number
    }
  ): Promise<Message> {
    console.log('🔄 Enviando via endpoint /admin-message')
    
    try {
      const payload: any = {
        sessionId,
        message,
        imageUrl,
        channel,
        author: 'agent'
      }
      
      // ✅ Adicionar campos extras se agentData fornecido
      if (agentData) {
        payload.conversationId = `conv-${sessionId}`
        payload.targetSessionId = sessionId
        payload.agentId = agentData.agentId || `agent-${agentData.agentEmail || 'unknown'}`
        payload.agentEmail = agentData.agentEmail
        payload.agentName = agentData.agentName
        payload.hotelId = agentData.hotelId
      }
      
      console.log('📋 Payload /admin-message:', payload)
      
      const response = await POST(`${BASE_URL}/admin-message`, payload)
      
      // 🔍 VERIFICAR SE BACKEND RESPEITOU O CANAL
      console.log('✅ Resposta /admin-message:', response)
      if (response.channel && response.channel !== channel) {
        console.error(`❌ BACKEND BUG (/admin-message): Enviamos channel="${channel}" mas backend retornou channel="${response.channel}"`)
      } else if (response.channel === channel) {
        console.log(`✅ Canal correto: "${channel}"`)
      }
      
      return response
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem do admin:', error)
      throw error
    }
  }

  /**
   * Enviar mensagem usando nova rota bidirecional com identificação do agente
   */
  static async sendBidirectionalMessage(
    sessionId: number,
    message: string,
    imageUrl?: string,
    channel: string = 'recepcao',
    agentData?: {
      agentId: string,
      agentEmail: string,
      agentName: string,
      hotelId: number
    }
  ): Promise<Message> {
    console.log('📤 CommunicationApi: Tentando enviar mensagem bidirecional')
    console.log('📤 Dados:', { sessionId, message, imageUrl, channel, agentData })
    
    try {
      // NOVA ROTA BIDIRECIONAL PRIMEIRO
      console.log('🔄 Tentando nova rota bidirecional...')
      const bidirectionalPayload = {
        sessionId: sessionId,                      // ID da sessão (será usado como agentSessionId)
        text: message,                             // Texto da mensagem
        author: "agent",                           // Sempre "agent"
        channel: channel,                          // 🎯 CANAL DO DEPARTAMENTO (turismo, financeiro, etc)
        conversationId: `conv-${sessionId}`,      // ID da conversa
        targetSessionId: sessionId,               // ID da sessão do GUEST
        agentId: agentData?.agentId || `agent-${agentData?.agentEmail || 'unknown'}`, // ID real do agent
        ...(imageUrl && { imageUrl }),            // URL da imagem (se houver)
        // Campos adicionais para rastreabilidade
        ...(agentData && {
          agentEmail: agentData.agentEmail,
          agentName: agentData.agentName,
          hotelId: agentData.hotelId
        })
      }
      
      console.log('📋 Payload bidirecional:', bidirectionalPayload)
      
      const bidirectionalResponse = await POST(
        `${BASE_URL}/messages/bidirectional`,
        bidirectionalPayload
      )
      
      console.log('✅ Sucesso na rota bidirecional:', bidirectionalResponse)
      
      // 🔍 APENAS LOGAR SE BACKEND RETORNOU CANAL ERRADO (não usar fallback automático)
      if (bidirectionalResponse.channel !== channel) {
        console.error(`❌ BACKEND BUG: Enviamos channel="${channel}" mas backend retornou channel="${bidirectionalResponse.channel}"`)
        console.warn(`⚠️ AVISO: Esta mensagem foi salva com canal incorreto no banco de dados!`)
      }
      
      return bidirectionalResponse
      
    } catch (bidirectionalError) {
      console.warn('⚠️ Falha na rota bidirecional, tentando fallback:', bidirectionalError)
      
      // FALLBACK PARA ROTA ANTIGA
      try {
        console.log('🔄 Usando rota antiga como fallback...')
        const fallbackResponse = await this.sendAdminMessage(sessionId, message, imageUrl, channel, agentData)
        
        console.log('✅ Sucesso no fallback:', fallbackResponse)
        return fallbackResponse
        
      } catch (fallbackError) {
        console.error('❌ Falha em ambas as rotas:', fallbackError)
        throw new Error(`Falha ao enviar mensagem: ${fallbackError}`)
      }
    }
  }

  /**
   * Buscar histórico completo de mensagens de uma sessão
   * ⚠️ MÉTODO DEPRECIADO - Use getMessages(hotelId, sessionKey) em vez disso
   */
  static async getSessionMessages(sessionId: number): Promise<Message[]> {
    // 🐛 DEBUG: Log para rastrear chamadas ao getSessionMessages
    console.log('🔍 DEBUG getSessionMessages chamado com sessionId:', sessionId)
    console.warn('⚠️ AVISO: getSessionMessages() está depreciado. Use getMessages(hotelId, sessionKey) em vez disso.')
    
    // ❌ MÉTODO INCORRETO - NÃO USAR MAIS
    // Este método não funciona porque o backend espera: /sessions/{hotelId}/{sessionKey}
    // Mas este método tenta fazer: /sessions/{sessionId}/messages
    throw new Error('getSessionMessages() está depreciado. Use getMessages(hotelId, sessionKey) em vez disso.')
  }

  /**
   * Salvar mensagem do Admin no banco (ChatMessage)
   */
  static async saveChatMessage(
    sessionId: number,
    text: string,
    imageUrl?: string,
    channel: string = 'recepcao'
  ): Promise<Message> {
    return POST(`${BASE_URL}/messages`, {
      sessionId,
      author: 'agent',
      text,
      imageUrl,
      channel,
      deliveryStatus: 'sent'
    })
  }

  /**
   * Upload de imagem para chat
   */
  static async uploadChatImage(file: File): Promise<{ imageUrl: string; url: string }> {
    const formData = new FormData()
    formData.append('image', file)
    
    // Importar axios para fazer upload com headers customizados
    const axios = (await import('axios')).default
    
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/upload/chat-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${this.getAuthToken()}`
      }
    })
    
    return response.data
  }

  /**
   * Obter token de autenticação
   */
  private static getAuthToken(): string {
    const loginData = localStorage.getItem('loginInfo_ninegrid_adm')
    if (loginData) {
      const parsed = JSON.parse(loginData)
      return parsed.accessToken
    }
    return ''
  }

  // ===== ESTATÍSTICAS =====

  /**
   * Obter estatísticas de comunicação de um hotel
   */
  static async getStats(hotelId: number): Promise<CommunicationStats> {
    return GET_INTERCEPTED(`${BASE_URL}/stats/${hotelId}`)
  }

  // ===== BUSCA =====

  /**
   * Buscar mensagens por termo
   */
  static async searchMessages(params: SearchParams): Promise<SearchResult[]> {
    const queryParams = new URLSearchParams({
      q: params.query,
      ...(params.department && { department: params.department }),
      ...(params.dateRange && { 
        startDate: params.dateRange.start.toISOString(),
        endDate: params.dateRange.end.toISOString()
      })
    })
    
    return GET_INTERCEPTED(`${BASE_URL}/search/${params.hotelId}?${queryParams}`)
  }

  // ===== INTELIGÊNCIA ARTIFICIAL =====

  /**
   * Analisar sentimento de um texto
   */
  static async analyzeSentiment(text: string): Promise<SentimentAnalysis> {
    return POST(`${BASE_URL}/sentiment-analysis`, { text })
  }

  /**
   * Obter sugestão de resposta da IA
   */
  static async getAIResponse(
    messageText: string, 
    sessionContext?: {
      department: Department
      guestName: string
      room: string
      previousMessages?: Message[]
    }
  ): Promise<AIResponseSuggestion> {
    return POST(`${BASE_URL}/ai-response`, { 
      messageText, 
      context: sessionContext 
    })
  }

  // ===== TRADUÇÃO =====

  /**
   * Traduzir texto
   */
  static async translateText(request: TranslationRequest): Promise<TranslationResponse> {
    return POST(`${BASE_URL}/translate`, request)
  }

  // ===== HEALTH CHECK =====

  /**
   * 🧪 Testar conectividade WebSocket do backend
   */
  static async testWebSocketConnectivity(): Promise<any> {
    console.log('🧪 Testando conectividade REST com:', `${BASE_URL.replace('/api', '')}`)
    
    try {
      // Teste 1: Verificar se a API REST está funcionando
      const healthCheck = await GET_INTERCEPTED('communication-center/health')
      console.log('✅ API REST funcionando:', healthCheck)
      
      // Teste 2: Verificar múltiplos endpoints Socket.IO possíveis
      console.log('🧪 Testando endpoints WebSocket...')
      
      const possibleEndpoints = [
        `${BASE_URL}/socket.io/`,           // /api/socket.io/
        `${BASE_URL.replace('/api', '')}/socket.io/`,  // /socket.io/
        `${BASE_URL.replace('/api', ':7004')}/socket.io/`,  // :7004/socket.io/
      ]
      
      let wsStatus = 404
      let workingEndpoint = null
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log('🔍 Testando:', endpoint)
          const wsResponse = await fetch(endpoint)
          console.log('📊 Status:', wsResponse.status)
          
          if (wsResponse.status !== 404) {
            wsStatus = wsResponse.status
            workingEndpoint = endpoint
            break
          }
        } catch (error) {
          console.log('❌ Falha em:', endpoint)
        }
      }
      
      console.log('🔍 Status WebSocket final:', wsStatus)
      console.log('🔍 Endpoint funcionando:', workingEndpoint)
      
      return {
        available: wsStatus !== 404,
        details: {
          apiUrl: BASE_URL,
          wsUrl: workingEndpoint || `${BASE_URL}/socket.io/`,
          restHealth: healthCheck,
          wsEndpointStatus: wsStatus,
          workingEndpoint,
          testedEndpoints: possibleEndpoints,
          timestamp: new Date().toISOString()
        }
      }
    } catch (error) {
      console.error('❌ Erro no teste de conectividade:', error)
      return {
        available: false,
        error: error instanceof Error ? error.message : String(error)
      }
    }
  }

  /**
   * Verificar status do serviço
   */
  static async healthCheck(): Promise<{ status: string; timestamp: Date }> {
    return GET_INTERCEPTED(`${BASE_URL}/health`)
  }

  /**
   * Debug: Teste de conectividade da API
   */
  static async debugApi(): Promise<void> {
    console.log('🔍 Debug API - Testando conectividade...')
    console.log('📍 BASE_URL:', BASE_URL)
    console.log('🌐 REACT_APP_API_URL:', process.env.REACT_APP_API_URL)
    
    // Importar api para ver a baseURL real
    const { default: axios } = await import('axios')
    console.log('🔧 Axios defaults.baseURL:', axios.defaults.baseURL)
    
    try {
      console.log('🏥 Testando health check...')
      const health = await this.healthCheck()
      console.log('✅ Health check OK:', health)
    } catch (error) {
      console.error('❌ Health check falhou:', error)
    }
    
    try {
      console.log('📋 Testando listagem de sessões...')
      const sessions = await this.getSessions()
      console.log('✅ Sessões carregadas:', sessions.length)
    } catch (error) {
      console.error('❌ Listagem de sessões falhou:', error)
    }
    
    try {
      console.log('💬 Testando mensagens diretamente...')
      const messages = await GET_INTERCEPTED('/communication-center/messages/1/1eae461b-9e69-4752-9b7f-e40c80e8c7fb')
      console.log('✅ Mensagens diretas carregadas:', messages.length)
    } catch (error) {
      console.error('❌ Mensagens diretas falharam:', error)
    }
  }

  // ===== MÉTODOS AUXILIARES =====

  /**
   * Construir URL com parâmetros de query
   */
  private static buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, item))
        } else {
          searchParams.append(key, value.toString())
        }
      }
    })
    
    return searchParams.toString()
  }

  /**
   * Buscar sessões com filtros avançados
   */
  static async getFilteredSessions(filters: {
    hotelId: number
    unreadOnly?: boolean
    connectedOnly?: boolean
    departments?: Department[]
    searchQuery?: string
    limit?: number
    offset?: number
  }): Promise<{ sessions: Session[]; total: number }> {
    const queryString = this.buildQueryString({
      hotelId: filters.hotelId,
      unreadOnly: filters.unreadOnly,
      connectedOnly: filters.connectedOnly,
      departments: filters.departments,
      q: filters.searchQuery,
      limit: filters.limit,
      offset: filters.offset
    })
    
    return GET_INTERCEPTED(`${BASE_URL}/sessions${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Buscar mensagens com paginação
   */
  static async getMessagesPaginated(
    hotelId: number, 
    sessionKey: string,
    options: {
      limit?: number
      offset?: number
      since?: Date
      until?: Date
    } = {}
  ): Promise<{ messages: Message[]; total: number; hasMore: boolean }> {
    try {
      // Por enquanto, usar o endpoint simples que existe
      const messages = await this.getMessages(hotelId, sessionKey)
      
      // Simular paginação até o backend implementar corretamente
      const limit = options.limit || 50
      const offset = options.offset || 0
      
      const paginatedMessages = messages.slice(offset, offset + limit)
      const hasMore = (offset + limit) < messages.length
      
      return {
        messages: paginatedMessages,
        total: messages.length,
        hasMore
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens paginadas:', error)
      
      // Fallback: retornar estrutura vazia
      return {
        messages: [],
        total: 0,
        hasMore: false
      }
    }
  }
}

// Export default para facilitar importação
export default CommunicationAPI